import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { HeaderTemplate, Stepper, TitleHeader, useSnackbarHandler } from 'web-library';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { useGetNonNationalApplicationStatus } from '../../../../api-refactored/non-national-exam-registration/useGetNonNationalApplicationStatus';
import { useGetNonNationalApplicationStatusDropdown } from '../../../../api-refactored/non-national-exam-registration/useGetNonNationalApplicationStatusDropdown';
import { useGetNonNationalExamSummary } from '../../../../api-refactored/non-national-exam-registration/useGetNonNationalExamSummary';
import { usePutNonNationalExamApplicationStatus } from '../../../../api-refactored/non-national-exam-registration/usePutNonNationalExamApplicationStatus';
import { useGetUserAuthorities } from '../../../../api-refactored/user/useGetUserAuthorities';
import LoadingComponent from '../../../../components/LoadingComponent';
import OpsAdminReviewApplication from '../../../../modules/examRegistration/components/nonNationalExam/OpsAdminReviewApplication';
import { existInArray } from '../../../../utils/ArrayUtils';
import NonNationalExamTypeUuidDisplay from '../NonNationalExamTypeUuidDisplay';
import useStyles from '../style';
import { useGetPassportDisabled } from '../../../../api-refactored/non-national-exam-registration/useGetPassportDisabled';
import { NonNexPassportDisableContext } from '../../../../routes/RootRouter';

interface RegistrationSummaryProps {
  routeSteps: { label: string | JSX.Element; disabled?: boolean }[];
}

interface SummaryPathParams {
  uuid: string;
}

const SpersRegistrationSummaryOpsAdmin = ({ routeSteps }: RegistrationSummaryProps) => {
  const { state } = useLocation<{ examTitle: string }>();
  const { uuid } = useParams<SummaryPathParams>();
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [isLoadingLastUpdated, setIsLoadingLastUpdated] = useState<boolean>(true);
  const [isLoadingApplicationStatus, setIsLoadingApplicationStatus] = useState<boolean>(true);
  const [
    isLoadingApplicationStatusDropdown,
    setIsLoadingApplicationStatusDropdown,
  ] = useState<boolean>(true);
  const [isLoadingExamSummary, setIsLoadingExamSummary] = useState<boolean>(true);
  const { snackbarHandler } = useSnackbarHandler();
  const [isPassportDisabled, setIsPassportDisabled] = useState<boolean>(false);
  const nonNexPassportDisableContext = useContext(NonNexPassportDisableContext);

  const authorities = useGetUserAuthorities();
  const hasEditAccess = existInArray(
    authorities.data,
    AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_EDIT
  );
  const {
    data: applicationStatusData,
    refetch: refetchGetNonNationalStatus,
  } = useGetNonNationalApplicationStatus({
    uuid,
    onSuccess: () => {
      setIsLoadingLastUpdated(false);
      setIsLoadingApplicationStatus(false);
    },
  });

  const { data: applicationStatusDropdown } = useGetNonNationalApplicationStatusDropdown({
    onSuccess: () => {
      setIsLoadingApplicationStatusDropdown(false);
    },
  });

  const { data } = useGetNonNationalExamSummary({
    uuid,
    onSuccess: () => {
      setIsLoadingExamSummary(false);
    },
  });

  const putNonNationalExamApplicationStatus = usePutNonNationalExamApplicationStatus({
    uuid,
    onSuccess: () => {
      if (!isLoadingLastUpdated) {
        setIsLoadingLastUpdated(true);
        refetchGetNonNationalStatus();
      }
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>NonNationalExam.ApplicationSuccessfullyUpdated'),
      });
    },
    onError: () => null,
  });

  useGetPassportDisabled({
    onSuccess: (res) => {
      setIsPassportDisabled(true);
      nonNexPassportDisableContext.setIsPassportDisabled(res);
    },
  });

  return (
    <>
      <TitleHeader title={t('exam->>NonNationalExam.ReviewApplication')} />
      <HeaderTemplate>
        <NonNationalExamTypeUuidDisplay nonExamType={state?.examTitle || '-'} enableApi={false} />
        {applicationStatusData?.editable && (
          <Stepper className={classes.stepper} activeStep={4} steps={routeSteps} />
        )}
      </HeaderTemplate>
      {!isLoadingExamSummary &&
      !isLoadingApplicationStatusDropdown &&
      !isLoadingApplicationStatus &&
      applicationStatusDropdown &&
      applicationStatusData ? (
        <>
          <OpsAdminReviewApplication
            data={data}
            isAEIS={false}
            opsAdminData={{
              putNonNationalExamApplicationStatus,
              applicationStatusDropdown,
              applicationStatusData,
            }}
            isLoadingLastUpdated={isLoadingLastUpdated}
            hasEditAccess={hasEditAccess}
          />
        </>
      ) : (
        <LoadingComponent />
      )}
    </>
  );
};

export default SpersRegistrationSummaryOpsAdmin;
